import { useEffect, useRef, useState } from 'react';
import apiClient from '../services/api';

/**
 * Fetches a project figure as a blob via the authenticated API client.
 * The JWT is sent in the Authorization header — never embedded in the URL.
 * The blob object-URL is revoked on unmount or when inputs change to prevent
 * memory leaks.
 */
export function useFigureBlob(
  projectId: string,
  figureId: string | null | undefined,
): { blobUrl: string | null; error: boolean } {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const blobRef = useRef<string | null>(null);

  useEffect(() => {
    if (!figureId || !projectId) return;

    let cancelled = false;
    setBlobUrl(null);
    setError(false);

    apiClient
      .get<Blob>(`/projects/${projectId}/figures/${figureId}/content`, {
        responseType: 'blob',
      })
      .then(res => {
        if (cancelled) return;
        if (blobRef.current) URL.revokeObjectURL(blobRef.current);
        const url = URL.createObjectURL(res.data);
        blobRef.current = url;
        setBlobUrl(url);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });

    return () => {
      cancelled = true;
      if (blobRef.current) {
        URL.revokeObjectURL(blobRef.current);
        blobRef.current = null;
      }
    };
  }, [projectId, figureId]);

  return { blobUrl, error };
}
